import { type SetNonNullable } from 'type-fest';

import { type Address } from './address.js';
import {
  type BaseItem,
  type MaritalStatus,
  type NullablePartial,
  type PhoneNumberKind,
} from './common.js';
import type {
  BaseEhrInformation,
  EhrInformation,
  CreateValues as EhrInformationCreateValues,
  UpdateValues as EhrInformationUpdateValues,
} from './ehrInformation.js';
import { type Employer } from './employer.js';
import { type SexualIdentity } from './patient/socialHistory.js';
import { Recommendation } from './patientAdvisory.js';
import { type Geometry } from './places.js';
import { type EmploymentStatus } from './practice/preference.js';
import { type Language, type LanguageWithOther } from './previsit/language.js';

export type { EhrInformation };
export { Recommendation };

export enum Ethnicity {
  Chicano = 'CHICANO',
  Cuban = 'CUBAN',
  Declined = 'PATIENT_DECLINED',
  Hispanic = 'HISPANIC',
  Mexican = 'MEXICAN',
  NonHispanic = 'NON-HISPANIC',
  PuertoRican = 'PUERTO_RICAN',
  Unknown = 'UNKNOWN',
}

// TODO: point everything to models/common/Sex, then kill this off
export enum Sex {
  Male = 'male',
  Female = 'female',
  Other = 'other',
}

export enum Race {
  African = 'AFRICAN',
  AfricanAmerican = 'AFRICAN_AMERICAN',
  AmericanIndian = 'AMERICAN_INDIAN',
  Asian = 'ASIAN',
  AsianIndian = 'ASIAN_INDIAN',
  Caucasian = 'CAUCASIAN',
  Chinese = 'CHINESE',
  Filipino = 'FILIPINO',
  Guamanian = 'GUAMANIAN',
  Japanese = 'JAPANESE',
  Korean = 'KOREAN',
  MiddleEasternOrNorthAfrican = 'MIDDLE_EASTERN_OR_NORTH_AFRICAN',
  Other = 'OTHER',
  PacificIslander = 'PACIFIC_ISLANDER',
  NativeHawaiian = 'NATIVE_HAWAIIAN',
  PatientDeclined = 'PATIENT_DECLINED',
  Samoan = 'SAMOAN',
  Vietnamese = 'VIETNAMESE',
  White = 'WHITE',
  Unavailable = 'UNAVAILABLE',
}

export enum InterpreterNeeded {
  Yes = 'YES',
  No = 'NO',
}

export enum VeteranStatus {
  ActiveDutyDependent = 'ACTIVE_DUTY_DEPENDENT',
  ActiveDuty = 'ACTIVE_DUTY',
  DeclineToAnswer = 'DECLINE_TO_ANSWER',
  Foreign = 'FOREIGN',
  Minor = 'MINOR',
  NationalGuard = 'NATIONAL_GUARD',
  NoNever = 'NO_NEVER',
  No = 'NO',
  Reserves = 'RESERVES',
  ReservistDependent = 'RESERVIST_DEPENDENT',
  RetiredDependent = 'RETIRED_DEPENDENT',
  Retired = 'RETIRED',
  Unavailable = 'UNAVAILABLE',
  Unknown = 'UNKNOWN',
  Veteran = 'VETERAN',
  Yes = 'YES',
}

export enum Religion {
  PreferNotToAnswer = 'PREFER_NOT_TO_ANSWER',
  Agnostic = 'AGNOSTIC',
  Christian = 'CHRISTIAN',
  Catholic = 'CATHOLIC',
  Mormon = 'MORMON',
  Jewish = 'JEWISH',
  Muslim = 'MUSLIM',
  JehovasWitness = 'JEHOVAS_WITNESS',
  Buddhist = 'BUDDHIST',
  Hindu = 'HINDU',
  ChristianScientists = 'CHRISTIAN_SCIENTISTS',
  OrthodoxJews = 'ORTHODOX_JEWS',
  SeventhDayAdventists = 'SEVENTH_DAY_ADVENTISTS',
  Amish = 'AMISH',
  Sikhs = 'SIKHS',
  Other = 'OTHER',
  None = 'NONE',
}

export enum EmergencyContactRelationship {
  AuntUncle = 'AUNT_UNCLE',
  Brother = 'BROTHER',
  Child = 'CHILD',
  Cousin = 'COUSIN',
  Daughter = 'DAUGHTER',
  DepChild = 'DEP_CHILD',
  DomesticPartnership = 'DOMESTIC_PARTNERSHIP',
  Donor = 'DONOR',
  Employee = 'EMPLOYEE',
  ExSpouse = 'EX_SPOUSE',
  Father = 'FATHER',
  FatherInLaw = 'FATHER_IN_LAW',
  FosterChild = 'FOSTER_CHILD',
  FosterParent = 'FOSTER_PARENT',
  Friend = 'FRIEND',
  GrandChild = 'GRANDCHILD',
  Granddaughter = 'GRANDDAUGHTER',
  Grandfather = 'GRANDFATHER',
  Grandmother = 'GRANDMOTHER',
  Grandparent = 'GRANDPARENT',
  Grandson = 'GRANDSON',
  GreatGrandfather = 'GREAT_GRANDFATHER',
  GreatGrandmother = 'GREAT_GRANDMOTHER',
  Guardian = 'GUARDIAN',
  InLaw = 'IN_LAW',
  LifePartner = 'LIFE_PARTNER',
  Mother = 'MOTHER',
  MotherInLaw = 'MOTHER_IN_LAW',
  Neighbor = 'NEIGHBOR',
  NieceNephew = 'NIECE_NEPHEW',
  Other = 'OTHER',
  OverageDependent = 'OVERAGE_DEPENDENT',
  Parent = 'PARENT',
  Recipient = 'RECIPIENT',
  Relative = 'RELATIVE',
  Roommate = 'ROOMMATE',
  Self = 'SELF',
  Sibling = 'SIBLING',
  SignificantOther = 'SIGNIFICANT_OTHER',
  Sister = 'SISTER',
  Son = 'SON',
  Sponsor = 'SPONSOR',
  Spouse = 'SPOUSE',
  Stepchild = 'STEPCHILD',
  Stepparent = 'STEPPARENT',
  Student = 'STUDENT',
  Ward = 'WARD',
}

export enum Pronoun {
  HeHimHis = 'HE_HIM_HIS',
  SheHerHers = 'SHE_HER_HERS',
  TheyThemTheirs = 'THEY_THEM_THEIRS',
  NoPronouns = 'NO_PRONOUNS',
  PleaseAsk = 'PLEASE_ASK',
  Other = 'OTHER',
}

export interface PronounFieldValue {
  pronoun?: Pronoun | null;
  pronounRaw?: string | null;
}

type JobAttribute = Partial<{
  preferredLastName: string;
  writtenLanguages: LanguageWithOther[];
  maritalStatus: MaritalStatus;
  fhirId: string;
  religion: Religion;
  religionRaw: string;
  ethnicity: Ethnicity;
  ethnicities: Ethnicity[];
  emergencyContactFirstName: string;
  emergencyContactLastName: string;
  emergencyContactRelationshipToPatientRaw: string;
  emergencyContactRelationshipToPatient: EmergencyContactRelationship;
  patientRelationshipToEmergencyContactRaw: string;
  patientRelationshipToEmergencyContact: EmergencyContactRelationship;
  emergencyContactPhone: string;
  employmentStatus: EmploymentStatus;
  employer: Employer;
  employerRaw: string;
  retirementDate: Date;
  employerPhone: string;
  grossAnnualIncome: number;
  dependents: number;
  veteranStatus: VeteranStatus;
  militaryDeployed: boolean;
  militaryFirstYearOfService: number;
  militaryCurrentlyServing: boolean;
  militaryLastYearOfService: number;
  interpreterNeeded: InterpreterNeeded;
  homeless: boolean;
  patientInstitutionId: string;
  mothersFirstName: string;
  useEmail: boolean;
  athenaIdxMrn: string;
  deceased: boolean;
  referralSource: string;
}>;

export interface AddressAttribute {
  mailingAddress: Required<Address> | null;
  physicalAddress: Required<Address> | null;
}

export interface UpsertAddressAttribute {
  mailingAddress?: Address | null;
  physicalAddress?: Address | null;
}

export interface BasePatient extends JobAttribute {
  patientEhrId?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: Date;
  phoneNumber?: string;
  cellPhoneNumber?: string;
  homePhoneNumber?: string;
  workPhoneNumber?: string;
  preferredPhoneNumber?: PhoneNumberKind;
  preferredPronoun?: Pronoun;
  preferredPronounRaw?: string;
  sex?: Sex;
  ssn4?: string;
  email?: string;
  race?: Race[];
  raceRaw?: string[];
  preferredFirstName?: string;
  preferredLanguage?: Language;
  birthGestationalAge?: string;
  birthWeightGrams?: number;
  genderIdentity?: SexualIdentity;
  genderIdentityRaw?: string;
}

export interface Patient extends BaseItem, BasePatient {}

export interface PatientWithEhrInformation {
  patient: Patient;
  ehrInformation: EhrInformation;
}

export interface BasePatientWithEhrInformationId extends BasePatient {
  ehrInformationId: string;
}

export type CreateValues = NullablePartial<BasePatient>;
export type UpdateValues = Partial<BasePatient>;

export type CreateWithEhrInformationValues = Omit<
  CreateValues,
  'mailingAddressId' | 'physicalAddressId'
> &
  UpsertAddressAttribute &
  Omit<EhrInformationCreateValues, 'patientId' | 'value'>;

export type UpdateWithEhrInformationValues = Omit<
  SetNonNullable<UpdateValues, 'patientEhrId'>,
  'mailingAddressId' | 'physicalAddressId'
> &
  UpsertAddressAttribute &
  Omit<EhrInformationUpdateValues, 'value'>;

export type BasePatientCombined = SetNonNullable<
  Omit<BasePatient, 'mailingAddressId' | 'physicalAddressId'>,
  'patientEhrId'
> &
  AddressAttribute &
  Pick<
    BaseEhrInformation,
    | 'practiceId'
    | 'deleted'
    | 'isTest'
    | 'createdByNotableInEhrTimestamp'
    | 'createdByNotableInEhrReason'
    | 'lastPatientEhrLoginTimestamp'
  >;

export type PatientForPatientData = Pick<
  BasePatientWithEhrInformationId,
  | 'ehrInformationId'
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
  | 'cellPhoneNumber'
  | 'homePhoneNumber'
  | 'workPhoneNumber'
  | 'preferredPhoneNumber'
  | 'sex'
  | 'email'
  | 'preferredLanguage'
> & {
  mailingAddress?: Geometry;
  physicalAddress?: Geometry;
};

type PatientAddress = {
  [key in keyof Pick<Address, 'address' | 'unitNumber' | 'city' | 'state' | 'zip'>]-?:
    | string
    | null;
};

export interface PatientWithAddress {
  patientInformation: BasePatient;
  patientAddressMailing: PatientAddress;
  patientAddressPhysical: PatientAddress;
}

// patient uploads types
export enum UploadKind {
  InsuranceCard = 'INSURANCE_CARD',
  DriversLicense = 'DRIVERS_LICENSE',
}

export interface Upload extends BaseItem {
  patientId: string;
  gsPath?: string;
  kind: UploadKind;
  uploaded: boolean;
  uploadedTimestamp?: Date;
  deleted: boolean;
  deletedTimestamp?: Date;
}

export enum PhoneNumberStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  New = 'NEW',
  Unsubscribed = 'UNSUBSCRIBED',
  Invalid = 'INVALID',
}

// https://www.twilio.com/docs/lookup/v2-api/line-type-intelligence#type-property-values
export enum PhoneNumberType {
  FixedVoip = 'FIXED_VOIP',
  Invalid = 'INVALID',
  Landline = 'LANDLINE',
  Mobile = 'MOBILE',
  NonFixedVoip = 'NON_FIXED_VOIP',
  NotableMobile = 'NOTABLE_MOBILE',
  Pager = 'PAGER',
  Personal = 'PERSONAL',
  Premium = 'PREMIUM',
  SharedCost = 'SHARED_COST',
  TollFree = 'TOLL_FREE',
  Uan = 'UAN',
  Unknown = 'UNKNOWN',
  Voicemail = 'VOICEMAIL',
}

// Used to define type below
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const phoneNumberFields = [
  'phoneNumber',
  'cellPhoneNumber',
  'homePhoneNumber',
  'workPhoneNumber',
] as const;
type PhoneNumberField = (typeof phoneNumberFields)[number];
export type PhoneNumberContact =
  | {
      number: string;
      normalizedNumber: string;
      status?: PhoneNumberStatus;
    }
  | { number: string; status: 'INVALID' };
export type ContactStatus = Partial<Record<PhoneNumberField, PhoneNumberContact>> & {
  email: {
    value?: string;
    subscribed?: boolean;
  };
};

export enum PatientMatchStrictness {
  Strict = 'Strict',
  TwoOrMore = 'TwoOrMore',
  ThreeOrMore = 'ThreeOrMore',
}
